import {AxiosError} from 'axios'
import {get, patch, post} from 'utils/axios'
import {
 CreateOrganisationPayload,
 CreateOrganisationResponse,
 GetOrganisationDetailsResponse,
 OrganisationDetailsDTO,
 OrganisationListResponse,
} from 'models/ViewOrganisation'
import {
 CreateOrganisationDocumentPayload,
 IOrganisationDocument,
 UpdateOrganisationDocumentPayload,
} from 'models/MyAccount'
import {handleRejectedErrorMsg} from 'utils/helpers/handleError'
import {toast} from 'react-toastify'
import {IFormField} from 'models/Common'
import {DefaultApiResponse} from 'models/ApiResponse'

type OrganisationDocumentsResponse =
 | IOrganisationDocument[]
 | {
    success?: boolean
    message?: string
    data?:
     | IOrganisationDocument[]
     | {results?: IOrganisationDocument[]}
    results?: IOrganisationDocument[]
   }

const normalizeOrganisationDocuments = (
 res: OrganisationDocumentsResponse | null | undefined
): IOrganisationDocument[] => {
 if (Array.isArray(res)) {
  return res
 }
 if (!res || typeof res !== 'object') {
  return []
 }
 if (Array.isArray(res.results)) {
  return res.results
 }
 if (Array.isArray(res.data)) {
  return res.data
 }
 if (
  res.data &&
  typeof res.data === 'object' &&
  Array.isArray(
   (res.data as {results?: IOrganisationDocument[]}).results
  )
 ) {
  return (res.data as {results: IOrganisationDocument[]})
   .results
 }
 return []
}

const getOrganisationListApi = async (
 params: IFormField
): Promise<OrganisationListResponse | null> => {
 try {
  const queryString = new URLSearchParams(params).toString()

  const res = await get<OrganisationListResponse>(
   `/rb/organisation/list?${queryString}`
  )

  return res || null
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return null
 }
}

const createOrganisationApi = async (
 payload: CreateOrganisationPayload
) => {
 try {
  const res = await post<CreateOrganisationResponse>(
   `/rb/organisation/upsert`,
   payload
  )
  if (res?.success) {
   toast.success(
    res?.message || 'Organisation created successfully'
   )
   return res
  }

  return null
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return null
 }
}

const getOrganisationDetailsApi = async (
 organisation_id: number
): Promise<OrganisationDetailsDTO | null> => {
 try {
  const res = await get<GetOrganisationDetailsResponse>(
   `/rb/organisation/detail?organisation_id=${organisation_id}`
  )

  if (res?.success) {
   return res.data
  }

  return null
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return null
 }
}

const updateOrganisationApi = async (
 payload: CreateOrganisationPayload & {org_id: number}
) => {
 try {
  const res = await patch<CreateOrganisationResponse>(
   `/rb/organisation/upsert`,
   payload
  )

  if (res?.success) {
   toast.success(
    res?.message || 'Organisation updated successfully'
   )
   return res
  }

  return null
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return null
 }
}

const fetchCommericialArea = async (
 district_id: number | null,
 sub_district_id: number | null
) => {
 try {
  return await get<DefaultApiResponse>(
   '/location/v1/commercial-areas',
   {
    district_id,
    sub_district_id,
   }
  ).then((res) => {
   if (res.success) {
    return res.data
   } else {
    toast.error(res.message)
    return null
   }
  })
 } catch (err: unknown) {
  const e = err as AxiosError
  handleRejectedErrorMsg(e)
  return []
 }
}

const getOrganisationDocumentsApi = async (
 organisation_id: number
): Promise<IOrganisationDocument[]> => {
 try {
  const res = await get<OrganisationDocumentsResponse>(
   `/rb/v1/organisation/documents?organisation_id=${organisation_id}`
  )

  return normalizeOrganisationDocuments(res)
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return []
 }
}

const createOrganisationDocumentApi = async (
 organisation_id: number,
 payload: CreateOrganisationDocumentPayload
) => {
 try {
  const res = await post<DefaultApiResponse>(
   `/rb/v1/organisation/documents?organisation_id=${organisation_id}`,
   payload
  )

  if (res?.success !== false) {
   return res
  }

  toast.error(res?.message || 'Failed to create document')
  return null
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return null
 }
}

const updateOrganisationDocumentApi = async (
 payload: UpdateOrganisationDocumentPayload
) => {
 try {
  const res = await patch<DefaultApiResponse>(
   `/rb/v1/organisation/document/update`,
   payload
  )

  if (res?.success !== false) {
   return res
  }

  toast.error(res?.message || 'Failed to update document')
  return null
 } catch (err) {
  handleRejectedErrorMsg(err as AxiosError)
  return null
 }
}

export {
 getOrganisationListApi,
 createOrganisationApi,
 getOrganisationDetailsApi,
 updateOrganisationApi,
 fetchCommericialArea,
 getOrganisationDocumentsApi,
 createOrganisationDocumentApi,
 updateOrganisationDocumentApi,
}
